import React, { FC, useMemo } from 'react'
import { useHistory, Route } from 'react-router-dom'
import { Box, Typography, makeStyles, Grid, Hidden } from 'transfix-ui/core'
import { ActionListItem, ActionList } from 'apps/fleet-planner/components/action-list'
import SetupGuideIllustration from 'apps/fleet-planner/components/illustrations/CarrierSetupGuide.svg'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { TSetupGuideStepType, ISetupGuideStep } from 'apps/fleet-planner/hooks/useGetSetupGuide'
import { useSetupGuide } from 'apps/fleet-planner/hooks/useSetupGuide'
import { updateUserOnboarding } from 'apps/fleet-planner/utils/api'
import { Button, Card } from 'transfix-ui'
import { CarrierSetupModal } from './CarrierSetupModal'

// Uses index value to order items
const ORDERED_STEPS: ISetupGuideStep['type'][] = [
  'carrier_onboarding',
  'create_load',
  'invite_team_members',
  'connect_eld',
]

/**
 * Returns filtered list of items to show in setup guide
 */
const getOrderedSteps = (steps: ISetupGuideStep[] = []): ISetupGuideStep[] => {
  if (!steps || steps.length < 2) return steps
  return steps
    .sort(({ type: typeA }, { type: typeB }) => {
      return ORDERED_STEPS.indexOf(typeA) - ORDERED_STEPS.indexOf(typeB)
    })
    .filter(step => !!step)
}

interface ISetupGuideCardProps {
  firstName: string
}

interface ISetupGuideCopy {
  title: string
  subtitle: string
  onClick: () => void
}

const useSetupGuideContainerStyles = makeStyles(theme => ({
  headerWrapper: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}))

export const SetupGuideContainer: FC<ISetupGuideCardProps> = ({ firstName }) => {
  const { trackEvent } = useFMSAnalytics()
  const {
    loading,
    getStepIcon,
    isSetupGuideComplete,
    steps = [],
    isSetupGuideDismissed,
  } = useSetupGuide()
  const history = useHistory()

  const SETUP_GUIDE_STEPS: Partial<Record<TSetupGuideStepType, ISetupGuideCopy>> = {
    create_load: {
      title: 'Add your first load',
      subtitle: 'Learn how to create a planned load and dispatch it to your driver.',
      onClick: () => {
        trackEvent('Setup Guide', 'CLICKED_ADD_YOUR_FIRST_LOAD_FROM_SETUP_GUIDE')
        history.push('/loads/create-load')
      },
    },
    invite_team_members: {
      title: 'Invite your drivers and dispatchers',
      subtitle: 'Invite your team members so they can start moving loads.',
      onClick: () => {
        trackEvent('Setup Guide', 'CLICKED_INVITE_YOUR_DRIVERS_AND_DISPATCHERS_FROM_SETUP_GUIDE')
        history.push('/team')
      },
    },
    carrier_onboarding: {
      title: 'Get your carrier approved to book loads',
      subtitle:
        'Sign our Broker-Carrier Agreement and submit your carrier packet in under 2 minutes.',
      onClick: () => {
        trackEvent('Setup Guide', 'CLICKED_GET_VERIFIED_TO_BOOK_LOADS_FROM_SETUP_GUIDE')
        history.push('/home/carrier-setup')
      },
    },
    connect_eld: {
      title: 'Connect your ELD for live tracking',
      subtitle:
        'Integrate your ELD for hassle-free tracking on your Transfix and non-Transfix loads.',
      onClick: () => {
        trackEvent('Setup Guide', 'CLICKED_CONNECT_ELD_LIVE_TRACKING')
        history.push('/user/settings/integrations')
      },
    },
  }

  const isComplete = isSetupGuideComplete()
  const header = isComplete ? `You're all set, ${firstName}!` : 'Get started on Transfix'

  const dismissSetupGuideHandler = async () => {
    if (!isComplete) return
    trackEvent('Setup Guide', 'DISMISSED_COMPLETED_SETUP_GUIDE')
    try {
      await updateUserOnboarding({
        onboarding: {
          setupGuideDismissed: true,
        },
      })
    } catch (e) {
      // no op
      // TODO: add catch
    }
  }

  /**
   * Trigger this only when `isComplete` is true
   */
  // TODO: This event misfires and tracks even though there are no setup guide displayed
  // useEffect(() => {
  //   if (isComplete) {
  //     trackEvent('Setup Guide', 'VIEWED_COMPLETED_SETUP_GUIDE')
  //   }
  // }, [isComplete])

  const closeModalHandler = () => history.push('/home')

  const classes = useSetupGuideContainerStyles()

  const isSetupGuideVisible = !isSetupGuideDismissed && !isComplete

  /**
   * Track whether the user has seen the setup guide
   */
  // TODO: This event misfires and tracks even though there are no setup guide displayed
  // useEffect(() => {
  //   if (isSetupGuideVisible) {
  //     trackEvent('Setup Guide', 'VIEWED_SETUP_GUIDE')
  //   }
  // }, [isSetupGuideVisible])

  const orderedSteps = useMemo(() => getOrderedSteps(steps), [steps])

  return (
    <>
      {isSetupGuideVisible && (
        <Card dataTest='setup-guide-card' noPadding>
          <Hidden smDown>
            <img src={SetupGuideIllustration} alt='Setup Guide Illustration' />
          </Hidden>
          <Box>
            <Box className={classes.headerWrapper}>
              <Typography variant='h3' component='div'>
                {header}
              </Typography>
            </Box>
            <Box>
              <ActionList>
                {(loading || !steps.length) && (
                  <>
                    <ActionListItem loading />
                    <ActionListItem loading />
                    <ActionListItem loading />
                  </>
                )}
                {!loading &&
                  orderedSteps.map(step => {
                    const { type } = step || {}
                    const { onClick, subtitle = '', title = '' } = SETUP_GUIDE_STEPS[type] || {}
                    const iconName = getStepIcon(type)
                    return (
                      <ActionListItem
                        key={type}
                        title={title}
                        subtitle={subtitle}
                        onClick={onClick}
                        iconName={iconName}
                      />
                    )
                  })}
              </ActionList>
            </Box>
            {isComplete && (
              <Grid container>
                <Grid item xs={12}>
                  <Box textAlign='right' pt={1.5} p={3}>
                    <Button
                      color='primary'
                      data-test='dismiss-setup-guide'
                      size='large'
                      onClick={dismissSetupGuideHandler}>
                      Dismiss
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
          {/**
           * Carrier Setup Modal
           */}
          <Route path='/home/carrier-setup' exact>
            <CarrierSetupModal onClose={closeModalHandler} />
          </Route>
        </Card>
      )}
    </>
  )
}
